import type { RouteRecordRaw } from 'vue-router'
import PassThrough from '@/components/layout/PassThrough.vue'
import { getUserRoutes } from '@/routes/user/user'
import Country from '@/data/models/domain/Country'
import Timezone from '@/data/models/domain/Timezone'
import { getAuthRoutes } from '@/routes/auth/auth'
import { getTenDlcTollFreeRoutes } from '@/routes/tenDlcTollFree'
import { getPaymentRoutes } from '@/routes/payment'
import { getWizardRoutes } from '@/routes/wizard'
import { PageAvailableFor } from '@/types'
import LoggedInPageResolver from '@/components/resolvers/LoggedInPageResolver.vue'
import { AppModule } from '@/config/types'
import { getByocTopRoutes } from '@/routes/user/services/byocTop'
import { getTokenIsLockedRoutes } from '@/routes/tokenIsLocked'
import { getMessengerPreviewRoutes } from '@/routes/user/messenger/messengerPreview'
import { getConsentRoutes } from '@/routes/user/consent/consent'
import User from '@/data/models/domain/User'
import type { FieldDataCacheClearResolverParams } from '@/services/resolvers/fieldDataCache/types'
import { CacheableFields } from '@/services/forms/fieldCache/types'
import Contact from '@/data/models/domain/Contact'
import { ticketsRouteNames } from '@/routes/user/tickets/types'
import TicketStatus from '@/data/models/domain/tickets/TicketStatus'
import Ticket from '@/data/models/domain/tickets/Ticket'
import { ticketIdViewParam } from '@/services/domain/tickets/const'
import type { DomainTitlerParams } from '@/services/route/titlers/types'
import TicketInbox from '@/data/models/domain/tickets/TicketInbox'
import type { TicketByWorkspaceIdResolverParams } from '@/services/domain/tickets/resolvers/ticketByWorkspaceIdResolverService'
import { getPrivacyPolicyRoutes } from '@/routes/privacyPolicy'
import type NpsSurvey from '@/components/views/external/NpsSurvey.vue'
import type { ComponentProps } from '@/composition/types'
import type { RouterBuilderInterface } from '@/services/route/types'

const routes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'root',
    component: PassThrough,
    meta: {
      titler: {
        service: 'DefaultTitlerService',
      },
      resolvers: [
        [
          {
            service: 'RootResolver',
          },
        ],
        {
          service: 'WizardPrivateUseCaseResolver',
        },
      ],
      modules: [AppModule.DateTime],
    },
    children: [
      {
        path: '',
        name: 'loggedInRootTrial',
        component: () => import('@/components/layout/LoggedInRootTrial.vue'),
        redirect: 'loggedInRootTrialSuccessLoadUser',
        children: [
          {
            path: '',
            name: 'loggedInRootTrialSuccessLoadUser',
            component: PassThrough,
            meta: {
              resolvers: [
                {
                  service: 'WizardResolverService',
                },
                {
                  service: 'WizardAnalyticsResolverService',
                },
                {
                  service: 'ContactServerEventsResolverService',
                },
                {
                  service: 'FieldsDataCacheClearByModelEventResolverService',
                  params: [
                    {
                      model: User,
                      fields: [CacheableFields.FIELD_ASSIGNEE],
                    },
                    {
                      model: Contact,
                      fields: [CacheableFields.FIELD_CONTACT_SELECT],
                    },
                  ] satisfies FieldDataCacheClearResolverParams,
                },
                {
                  service: 'PosthogRecordingServiceResolver',
                },
                {
                  service: 'UserPresenceResolver',
                },
                {
                  service: 'FieldAssigneeCacheClearResolverService',
                },
                {
                  service: 'TicketCountSubscriptionResolverService',
                },
              ],
              modules: [
                AppModule.CustomFields,
                AppModule.SubAccountsBase,
                AppModule.InvoicesBase,
                AppModule.BillingBase,
                AppModule.VoiceCall,
                AppModule.ContactsBase,
                AppModule.ChatStatistics,
                AppModule.ChatPreviewNext,
                AppModule.MessagesBulksProgress,
                AppModule.TicketsNotifications,
                AppModule.TmTiptapEditor,
                AppModule.ScheduledEmail,
                AppModule.Templates,
              ],
            },
            children: [
              {
                path: '',
                name: 'loggedInRoot',
                component: () => import('@/components/layout/LoggedInRoot.vue'),
                meta: {
                  resolverComponent: LoggedInPageResolver,
                  resolvers: [
                    {
                      service: 'FilterSchemaResolver',
                    },
                    {
                      service: 'CustomFieldSchemaResolver',
                    },
                    {
                      service: 'ModelResolver',
                      params: [Country, Timezone],
                    },
                    [
                      {
                        service: 'UserResolver',
                      },
                      {
                        service: 'PermissionsResolverService',
                      },
                      {
                        service: 'PermissionsSubscriptionResolverService',
                      },
                      {
                        service: 'SubAccountsResolver',
                      },
                      {
                        service: 'MarketingDataResolverService',
                      },
                    ],
                    {
                      service: 'ChatStatisticsResolver',
                    },
                    {
                      service: 'TicketMessageNotificationResolver',
                    },
                    {
                      service: 'VoiceCallResolverService',
                    },
                    {
                      service: 'ByocAccessResolver',
                    },
                    {
                      service: 'SystemExitResolver',
                    },
                    {
                      service: 'StopImpersonateResolver',
                    },
                    {
                      service: 'UserCustomFieldsEventResolver',
                    },
                    {
                      service: 'SystemRefreshResolver',
                    },
                    {
                      service: 'UiSettingsResolverService',
                    },
                    {
                      service: 'UserWorkflowAccountChangedResolverService',
                    },
                    {
                      service: 'UserPolicyPrivacyChangedResolverService',
                    },
                    {
                      service: 'TermsUpdateResolverService',
                    },
                    {
                      service: 'RouteLeaveResolver',
                    },
                    {
                      service: 'BonusInfoResolver',
                    },
                  ],
                },
                children: [
                  {
                    name: 'user.top',
                    path: '',
                    component: PassThrough,
                    children: [
                      ...getTenDlcTollFreeRoutes(),
                      ...getPaymentRoutes(),
                      ...getPrivacyPolicyRoutes(),
                      ...getUserRoutes(),
                      ...getByocTopRoutes(),
                      {
                        name: 'ticketsDetailsWindow',
                        path: 'ticket-details-window/:ticketId',
                        component: () => import('@/components/views/tickets/TicketDetailsWindow.vue'),
                        meta: {
                          modules: [AppModule.Typing, AppModule.Note],
                          resolvers: [
                            {
                              service: 'ModelResolver',
                              params: [TicketStatus, TicketInbox],
                            },
                            {
                              service: 'TicketServerEventsResolverService',
                            },
                            {
                              service: 'TicketDetailsServerEventsResolverService',
                            },
                            {
                              service: 'TypingResolver',
                            },
                            [
                              {
                                service: 'TicketByWorkspaceIdResolverService',
                                params: {
                                  redirectToIfNotFound: { name: ticketsRouteNames.root },
                                } satisfies TicketByWorkspaceIdResolverParams,
                              },
                              {
                                service: 'TicketMarkAsReadResolverService',
                              },
                            ],
                          ],
                          resolverComponent: () =>
                            import('@/components/domain/tickets/resolvers/TicketDetailsWindowResolver.vue'),
                          titler: {
                            service: 'DomainTitlerService',
                            params: {
                              model: Ticket,
                              field: 'subject',
                              idParamName: ticketIdViewParam,
                            } as DomainTitlerParams,
                          },
                        },
                      },
                    ],
                  },
                ],
              },
              ...getWizardRoutes(),
              ...getTokenIsLockedRoutes(),
              ...getMessengerPreviewRoutes(),
              ...getConsentRoutes(),
            ],
          },
        ],
      },
      {
        name: 'auth.top',
        path: '',
        component: PassThrough,
        children: getAuthRoutes(),
      },
      {
        name: 'emailUnsubscribeNew',
        path: '/user/unsubscribe/:token',
        props: true,
        component: () => import('@/components/views/external/EmailUnsubscribe.vue'),
        meta: {
          pageAvailableFor: PageAvailableFor.all,
          modules: [AppModule.EmailUnsubscribe],
        },
      },
      {
        name: 'emailCampaignUnsubscribePage',
        path: '/email-campaigns/unsubscribe/:token',
        props: true,
        component: () => import('@/components/views/external/EmailCampaignUnsubscribe.vue'),
        meta: {
          pageAvailableFor: PageAvailableFor.all,
          modules: [AppModule.EmailCampaignUnsubscribePage],
        },
      },
      {
        name: 'widget',
        path: 'widget',
        component: PassThrough,
        redirect: { name: '' },
        meta: {
          pageAvailableFor: PageAvailableFor.all,
        },
        children: [
          {
            name: 'widget.subscribe',
            path: 'subscribe/:code',
            props: true,
            component: () => import('@/components/views/services/forms/FormSubscribeLanding.vue'),
            meta: {
              modules: [AppModule.WidgetSubscribe],
              resolvers: [{ service: 'WidgetEmbedCodeResolver' }],
            },
          },
        ],
      },
      {
        name: 'facebookDataDeletionStatusPage',
        path: '/facebook/data-deletion/status/:token',
        props: true,
        component: () => import('@/components/views/external/FacebookDataDeletionStatus.vue'),
        meta: {
          pageAvailableFor: PageAvailableFor.all,
          modules: [],
        },
      },
      {
        name: 'npsSurvey',
        path: '/nps-survey/:id/:score',
        props: (route) => {
          return {
            id: route.params.id,
            score: route.params.score,
          } satisfies ComponentProps<typeof NpsSurvey>
        },
        component: () => import('@/components/views/external/NpsSurvey.vue'),
        meta: {
          pageAvailableFor: PageAvailableFor.all,
          modules: [AppModule.NpsSurvey],
        },
      },
    ],
  },
  {
    path: '/redirect',
    name: 'redirect',
    component: () => import('@/components/views/Redirect.vue'),
    meta: {
      modules: [AppModule.ExternalIntegrations],
      resolvers: [
        {
          service: 'RedirectResolverService',
        },
      ],
    },
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('@/components/views/Error.vue'),
    meta: {
      pageAvailableFor: PageAvailableFor.all,
    },
  },
  {
    path: '/:catchAll(.*)*',
    redirect: '/',
  },
]

// A static import would ship ~150 KB of sandbox views to production,
// so we're using a dynamic one, wrapped inside the simple check that will trigger Rolldown's DCE mechanism.
export const addSandboxRoutes = async (routerBuilder: RouterBuilderInterface) => {
  if (import.meta.env.VUE_APP_SHOW_SANDBOX !== 'true') {
    return
  }

  const { sandboxRoute } = await import('@/routes/sandbox')

  routerBuilder.addRoute('user.top', sandboxRoute as RouteRecordRaw)
}

export default routes
